import { useRef, useState } from "react"
import axios from "axios"
import Loader from "../../components/Loader"
import { toast } from "sonner"

const V3Reports = () => {
  const fileInputRef = useRef(null)
  const [error, setError] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  const onBrowse = () => fileInputRef.current.click()

  const onFileChange = (event) => {
    setError(null)
    setSuccessMessage(null)
    setPdfUrl(null)
    const file = event.target.files[0]
    if (file && file.type === "application/pdf") {
      setSelectedFile(file)
    } else {
      setSelectedFile(null)
      toast.error("Invalid file selected. Please select a PDF file.")
      return
    }
    event.target.value = null
  }

  const processV2Report = async (file) => {
    if (!file) {
      toast.error("Please select a V2 Report PDF file.")
      return
    }

    setLoading(true)
    try {
      const formData = new FormData()
      formData.append("file", file)

      const response = await axios.post(
        `${process.env.REACT_APP_OUTSIDE_V3_REPORTS_BACKEND_URL}/v3-reports/upload/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${process.env.REACT_APP_OUTSIDE_V3_REPORTS_BACKEND_URL_AUTH_TOKEN}`,
          },
        }
      )

      const { b64_file, message, description } = response.data

      // Convert base64 to blob
      const byteCharacters = atob(b64_file)
      const byteArrays = []
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArrays.push(byteCharacters.charCodeAt(i))
      }
      const blob = new Blob([new Uint8Array(byteArrays)], {
        type: "application/pdf",
      })
      const url = window.URL.createObjectURL(blob)
      setPdfUrl(url)
      toast.success(message || "V3 Report generated successfully.")
      description && toast.success(description)
    } catch (error) {
      console.error("Error:", error)
      toast.error(
        "Something went wrong while processing your request. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  const downloadV3Report = () => {
    const newFileName =
      selectedFile?.name?.split(/\.pdf$/i)[0] || "anonymous_report"
    const a = document.createElement("a")
    a.href = pdfUrl
    a.download = `${newFileName}_v3.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(pdfUrl)
    // Clear the form
    setSelectedFile(null)
    setPdfUrl(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    await processV2Report(selectedFile)
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) {
      return bytes + " B"
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(2) + " KB"
    } else {
      return (bytes / (1024 * 1024)).toFixed(2) + " MB"
    }
  }

  return (
    <div className="flex items-center justify-center pt-6 pb-10">
      <div className="w-full max-w-5xl bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 py-4 px-6">
          <h1 className="text-2xl font-bold text-white text-center">
            V3 Reports
          </h1>
          <p className="text-blue-100 text-center text-sm mt-1">
            Upload your Original Report to generate the V3 Report.
          </p>
        </div>

        {/* Content Container */}
        <div className="px-6 py-4 md:px-8 md:py-4">
          <form className="space-y-8" onSubmit={handleSubmit}>
            {/* V2 Report PDF */}
            <div>
              <label className="block font-medium text-gray-800 mb-2">
                Original Report PDF <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileInputRef}
                hidden
                accept="application/pdf"
                type="file"
                onChange={onFileChange}
              />
              {!selectedFile ? (
                <button
                  className="w-full border-2 border-dashed border-primary/30 rounded-lg px-4 py-6 flex flex-col items-center justify-center text-primary hover:border-primary/80 transition-colors focus:outline-none focus:border-primary/80"
                  type="button"
                  onClick={onBrowse}
                >
                  <svg
                    className="w-8 h-8 mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  </svg>
                  <span className="text-sm font-medium">Select PDF</span>
                  <span className="text-xs text-gray-500 mt-1">
                    Click to browse the folder
                  </span>
                </button>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 flex items-center gap-3 bg-gray-50">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <svg
                      className="w-6 h-6 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      ></path>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                  <button
                    className="p-1 rounded-full hover:bg-gray-200 focus:outline-none disabled:cursor-not-allowed"
                    disabled={loading}
                    title="Remove file"
                    type="button"
                    onClick={() => setSelectedFile(null)}
                  >
                    <svg
                      className="w-5 h-5 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 18L18 6M6 6l12 12"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      ></path>
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Conditional rendering for download button if already processed */}
            {pdfUrl && !loading ? (
              <div className="flex flex-col items-center mt-4">
                <button
                  className="py-3 px-8 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg flex items-center gap-2 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-800 focus:ring-offset-2"
                  type="button"
                  onClick={downloadV3Report}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    ></path>
                  </svg>
                  Download V3 Report
                </button>
              </div>
            ) : (
              <div className="pt-4 flex justify-center">
                <button
                  className="py-3 px-8 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg flex justify-center items-center gap-2 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-800 focus:ring-offset-2 w-full max-w-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !selectedFile}
                  type="submit"
                >
                  {loading ? (
                    <Loader className="h-5 w-5" />
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      ></path>
                    </svg>
                  )}
                  {loading ? "Processing..." : "Generate V3 Report"}
                </button>
              </div>
            )}
          </form>

          {successMessage && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm text-center">
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  ></path>
                </svg>
                <span>{successMessage}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm text-center">
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  ></path>
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default V3Reports
